import { useState, useEffect, useCallback, useMemo } from "react";
import { AppLayout } from "../../components/AppLayout";
import { NotificationSettingsDialog } from "../../components/NotificationSettingsDialog";
import { MonthCalendar } from "../../components/MonthCalendar";
import { slotsForDate } from "../../lib/schedule";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import {
  Search, Stethoscope, MapPin, Navigation, Info, CalendarPlus, Clock, Loader2, Ban,
  Repeat, ArrowRightLeft, Check, X, History, CalendarCheck, Building2, FileDown, BookText,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function fmtDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
}

const EXCHANGE_STATUS = {
  accepted: { label: "Accettata", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rifiutata", cls: "bg-red-100 text-red-600" },
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-700" },
};

function MapsLink({ address, testid }) {
  const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title="Apri il percorso in Google Maps"
      className="mt-0.5 flex w-fit items-center gap-1 text-xs text-slate-500 underline-offset-2 transition-colors hover:text-blue-700 hover:underline"
      data-testid={testid}>
      <MapPin className="h-3.5 w-3.5 shrink-0" /> {address}
      <Navigation className="h-3 w-3 shrink-0 text-blue-600" />
    </a>
  );
}

export default function IsfDashboard() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [medici, setMedici] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [q, setQ] = useState("");
  const [spec, setSpec] = useState("all");
  const [holidays, setHolidays] = useState({});
  const [bookingMedico, setBookingMedico] = useState(null);
  const [appts, setAppts] = useState({ active: [], history: [] });
  const [exchanges, setExchanges] = useState({ incoming: [], outgoing: [] });
  const [exchangeFor, setExchangeFor] = useState(null); // {medicoId, myApptId}
  const [apptDay, setApptDay] = useState(null);
  const bookingDates = useMemo(() => (bookingMedico ? appts.active.filter((a) => a.medico_id === bookingMedico.id).map((a) => a.date) : []), [appts, bookingMedico]);
  const visitDates = useMemo(() => new Set([...appts.active, ...appts.history.filter((a) => a.status === "confirmed")].map((a) => a.date)), [appts]);
  const shownActive = apptDay ? appts.active.filter((a) => a.date === apptDay) : appts.active;
  const shownHistory = apptDay ? appts.history.filter((a) => a.date === apptDay) : appts.history;

  const loadMedici = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (spec && spec !== "all") params.spec = spec;
    const { data } = await api.get("/isf/medici", { params });
    setMedici(data.medici);
    setSpecs(data.specializzazioni);
  }, [q, spec]);

  const loadAppts = useCallback(async () => {
    const { data } = await api.get("/isf/appointments");
    setAppts(data);
  }, []);
  const loadExchanges = useCallback(async () => {
    const { data } = await api.get("/isf/exchange");
    setExchanges(data);
  }, []);

  const downloadAgenda = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (e) { toast.error("Errore durante l'esportazione"); }
  };

  useEffect(() => { loadMedici(); }, [loadMedici]);
  useEffect(() => { loadAppts(); loadExchanges(); }, [loadAppts, loadExchanges]);
  useEffect(() => {
    const start = todayIso();
    const end = `${new Date().getFullYear() + 1}-12-31`;
    api.get("/holidays", { params: { start, end } }).then(({ data }) => {
      const map = {};
      data.holidays.forEach((h) => { map[h.date] = h.name; });
      setHolidays(map);
    });
  }, []);

  const incomingCount = exchanges.incoming.length;

  return (
    <AppLayout title="Dashboard Informatore" onOpenSettings={() => setSettingsOpen(true)}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-800 text-slate-900">Prenota i tuoi appuntamenti</h1>
          <p className="text-sm text-slate-500">Consulta i medici operativi nella provincia di Como.</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => downloadAgenda("/guides/isf", "Guida-ISF-Agenda-ISF-COMO.pdf")} data-testid="isf-guide-btn">
          <BookText className="h-4 w-4" /> Guida ISF (PDF)
        </Button>
      </div>

      <Tabs defaultValue="catalogo">
        <TabsList>
          <TabsTrigger value="catalogo" data-testid="tab-catalogo"><Stethoscope className="mr-1.5 h-4 w-4" /> Catalogo Medici</TabsTrigger>
          <TabsTrigger value="appuntamenti" data-testid="tab-appuntamenti"><CalendarCheck className="mr-1.5 h-4 w-4" /> I Miei Appuntamenti</TabsTrigger>
          <TabsTrigger value="scambi" data-testid="tab-scambi">
            <Repeat className="mr-1.5 h-4 w-4" /> Scambi
            {incomingCount > 0 && <Badge className="ml-2 bg-amber-500">{incomingCount}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* CATALOGO */}
        <TabsContent value="catalogo" className="mt-5">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cerca per nome o specializzazione..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="medici-search-input" />
            </div>
            <Select value={spec} onValueChange={setSpec}>
              <SelectTrigger className="w-full sm:w-64" data-testid="spec-filter-select">
                <SelectValue placeholder="Tutte le specializzazioni" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le specializzazioni</SelectItem>
                {specs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {medici.length === 0 ? (
              <p className="col-span-full rounded-lg border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">Nessun medico trovato.</p>
            ) : medici.map((m, i) => (
              <div key={m.id} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-sm animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`medico-card-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 font-heading font-700 text-blue-700">
                    {m.nome[0]}{m.cognome[0]}
                  </div>
                  <div>
                    <p className="font-heading font-700 text-slate-900">Dr. {m.nome} {m.cognome}</p>
                    <p className="text-sm text-blue-700">{m.specializzazione}</p>
                    {m.indirizzo && <MapsLink address={m.indirizzo} testid={`medico-address-${m.id}`} />}
                  </div>
                </div>
                {m.note && (
                  <p className="mt-2 flex items-start gap-1.5 rounded-md bg-sky-50 p-2 text-xs text-slate-600">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" /> {m.note}
                  </p>
                )}
                <Button className="mt-4 w-full rounded-full bg-blue-700 hover:bg-blue-800" onClick={() => setBookingMedico(m)} data-testid={`book-btn-${m.id}`}>
                  <CalendarPlus className="mr-2 h-4 w-4" /> Prenota
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* APPUNTAMENTI */}
        <TabsContent value="appuntamenti" className="mt-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-heading font-700 text-slate-900">Appuntamenti attivi</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => downloadAgenda("/isf/agenda/pdf", "miei-appuntamenti.pdf")} data-testid="isf-export-pdf-btn">
                <FileDown className="h-3.5 w-3.5" /> Esporta PDF
              </Button>
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => downloadAgenda("/isf/agenda/ics", "miei-appuntamenti.ics")} data-testid="isf-export-ics-btn">
                <CalendarCheck className="h-3.5 w-3.5" /> Esporta ICS
              </Button>
            </div>
          </div>
          <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
          <div>
            <MonthCalendar value={apptDay} onSelect={(d) => setApptDay(apptDay === d ? null : d)} getMarker={(d) => (visitDates.has(d) ? "appointment" : null)} />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid="appts-calendar-legend">
              <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-3 w-3 rounded bg-emerald-500" /> Giorno di visita</span>
              {apptDay && (
                <button type="button" className="text-[11px] font-600 text-blue-700 hover:underline" onClick={() => setApptDay(null)} data-testid="appts-show-all-btn">
                  Mostra tutti
                </button>
              )}
            </div>
          </div>
          <div>
          {apptDay && (
            <p className="mb-3 text-sm text-slate-500" data-testid="appts-day-filter">Appuntamenti di <span className="font-600 capitalize text-slate-900">{fmtDate(apptDay)}</span></p>
          )}
          <div className="space-y-2">
            {shownActive.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">{apptDay ? "Nessun appuntamento attivo in questo giorno." : "Nessun appuntamento attivo."}</p>
            ) : shownActive.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4" data-testid={`active-appt-${a.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 flex-col items-center justify-center rounded-lg bg-sky-50 text-blue-700">
                    <Clock className="h-4 w-4" /><span className="text-[11px] font-700">{a.time}</span>
                  </div>
                  <div>
                    <p className="font-600 text-slate-900">Dr. {a.medico_nome}</p>
                    <p className="text-xs text-slate-500">{a.medico_specializzazione} · <span className="capitalize">{fmtDate(a.date)}</span></p>
                    {a.medico_indirizzo && <MapsLink address={a.medico_indirizzo} testid={`appt-address-${a.id}`} />}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setExchangeFor({ medicoId: a.medico_id, myApptId: a.id, medicoNome: a.medico_nome })} data-testid={`propose-exchange-btn-${a.id}`}>
                    <ArrowRightLeft className="h-3.5 w-3.5" /> Scambia
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" data-testid={`cancel-btn-${a.id}`}>
                        <Ban className="mr-1 h-3.5 w-3.5" /> Cancella
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancellare la prenotazione?</AlertDialogTitle>
                        <AlertDialogDescription>Il Dr. {a.medico_nome} riceverà una notifica di disdetta. Potrai prenotare nuovamente dal giorno successivo.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => {
                          try { await api.post(`/isf/appointments/${a.id}/cancel`); toast.success("Prenotazione cancellata"); loadAppts(); }
                          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                        }} data-testid={`confirm-cancel-${a.id}`}>Cancella</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>

          <h3 className="mb-3 mt-8 flex items-center gap-1.5 font-heading font-700 text-slate-900"><History className="h-4 w-4" /> Storico</h3>
          <div className="space-y-2" data-testid="history-list">
            {shownHistory.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">Nessuno storico.</p>
            ) : shownHistory.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm">
                <div>
                  <span className="font-600 text-slate-700">Dr. {a.medico_nome}</span>
                  <span className="ml-2 capitalize text-slate-400">{fmtDate(a.date)} · {a.time}</span>
                  {a.medico_indirizzo && <MapsLink address={a.medico_indirizzo} testid={`history-address-${a.id}`} />}
                </div>
                <Badge variant="secondary" className={a.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-slate-200 text-slate-600"}>
                  {a.status === "cancelled" ? "Annullato" : "Completato"}
                </Badge>
              </div>
            ))}
          </div>
          </div>
          </div>
        </TabsContent>

        {/* SCAMBI */}
        <TabsContent value="scambi" className="mt-5">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 font-heading font-700 text-slate-900">Proposte ricevute</h3>
              <div className="space-y-2" data-testid="incoming-exchanges">
                {exchanges.incoming.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nessuna proposta ricevuta.</p>
                ) : exchanges.incoming.map((ex) => (
                  <div key={ex.id} className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid={`incoming-ex-${ex.id}`}>
                    <p className="text-sm text-slate-700"><span className="font-600">{ex.from_isf_nome}</span> propone uno scambio:</p>
                    <div className="my-2 flex items-center gap-2 text-xs">
                      <span className="rounded-md bg-white px-2 py-1 text-slate-600">Tuo: {ex.target_slot}</span>
                      <ArrowRightLeft className="h-4 w-4 text-amber-600" />
                      <span className="rounded-md bg-white px-2 py-1 text-slate-600">Suo: {ex.from_slot}</span>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={async () => {
                        try { await api.post(`/isf/exchange/${ex.id}/accept`); toast.success("Scambio effettuato"); loadExchanges(); loadAppts(); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }} data-testid={`accept-ex-${ex.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Accetto</Button>
                      <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" onClick={async () => {
                        try { await api.post(`/isf/exchange/${ex.id}/reject`); toast.success("Proposta rifiutata"); loadExchanges(); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }} data-testid={`reject-ex-${ex.id}`}><X className="mr-1 h-3.5 w-3.5" /> Rifiuto</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-3 font-heading font-700 text-slate-900">Proposte inviate</h3>
              <div className="space-y-2" data-testid="outgoing-exchanges">
                {exchanges.outgoing.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nessuna proposta inviata. Vai su "I Miei Appuntamenti" e usa "Scambia".</p>
                ) : exchanges.outgoing.map((ex) => (
                  <div key={ex.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4 text-sm">
                    <div>
                      <p className="text-slate-700">A <span className="font-600">{ex.to_isf_nome}</span></p>
                      <p className="text-xs text-slate-400">{ex.from_slot} ↔ {ex.target_slot}</p>
                    </div>
                    <Badge className={EXCHANGE_STATUS[ex.status]?.cls || EXCHANGE_STATUS.pending.cls}>
                      {EXCHANGE_STATUS[ex.status]?.label || EXCHANGE_STATUS.pending.label}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {bookingMedico && (
        <BookingDialog medico={bookingMedico} holidays={holidays} myDates={bookingDates} onClose={() => setBookingMedico(null)} onBooked={() => { setBookingMedico(null); loadAppts(); }} />
      )}
      {exchangeFor && (
        <ExchangeDialog info={exchangeFor} onClose={() => setExchangeFor(null)} onProposed={() => { setExchangeFor(null); loadExchanges(); }} />
      )}
      <NotificationSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </AppLayout>
  );
}

function BookingDialog({ medico, holidays, myDates, onClose, onBooked }) {
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const unlocked = medico.unlocked_days || [];
  const vacation = medico.vacation_days || [];
  const blockedDays = medico.admin_blocked_days || [];

  const maxMonth = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth() + 12); return d; }, []);

  const isDisabled = (d) => {
    if (d < todayIso()) return true;
    const wd = (new Date(d + "T00:00:00").getDay() + 6) % 7;
    if (vacation.includes(d)) return true;
    if (blockedDays.includes(d)) return true;
    if (holidays[d] && !unlocked.includes(d)) return true;
    if (wd >= 5 && !unlocked.includes(d)) return true;
    return false;
  };
  const getMarker = (d) => {
    if (d < todayIso()) return null;
    if (myDates.includes(d)) return "appointment";
    const wd = (new Date(d + "T00:00:00").getDay() + 6) % 7;
    if (blockedDays.includes(d)) return "blocked";
    if (vacation.includes(d)) return "vacation";
    if (holidays[d] && !unlocked.includes(d)) return "holiday";
    if (wd >= 5 && !unlocked.includes(d)) return "weekend";
    const slots = slotsForDate(medico, d);
    if (slots.length > 0) return "reception";
    return null;
  };

  const selectDate = async (d) => {
    setDate(d); setSlots([]); setLoadingSlots(true);
    try {
      const { data } = await api.get(`/isf/medici/${medico.id}/slots`, { params: { date: d } });
      setSlots(data.slots);
    } finally { setLoadingSlots(false); }
  };

  const book = async (time) => {
    setBooking(true);
    try {
      await api.post("/isf/appointments", { medico_id: medico.id, date, time });
      toast.success("Appuntamento confermato!");
      onBooked();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBooking(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Prenota con Dr. {medico.nome} {medico.cognome}</DialogTitle>
          <DialogDescription>{medico.specializzazione} · max {medico.max_appointments_per_day} appuntamenti/giorno</DialogDescription>
        </DialogHeader>
        {medico.note && (
          <p className="flex items-start gap-1.5 rounded-md bg-sky-50 p-2.5 text-xs text-slate-600">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" /> {medico.note}
          </p>
        )}
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <MonthCalendar value={date} onSelect={selectDate} isDisabled={isDisabled} getMarker={getMarker} maxMonth={maxMonth} />
            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid="booking-legend">
              <span className="flex items-center gap-1.5 text-[11px] text-slate-600" data-testid="legend-my-appointment"><span className="h-3 w-3 rounded bg-emerald-500" /> Il tuo appuntamento</span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-3 w-3 rounded bg-sky-500" /> Disponibile</span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-3 w-3 rounded bg-rose-400" /> Weekend</span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-3 w-3 rounded bg-rose-600" /> Festività</span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-3 w-3 rounded bg-amber-400" /> Assenza</span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-3 w-3 rounded bg-slate-800" /> Non prenotabile</span>
            </div>
          </div>
          <div>
            <p className="mb-2 text-sm font-600 text-slate-900">
              {date ? <span className="capitalize">{fmtDate(date)}</span> : "Seleziona una data"}
            </p>
            <SlotsPanel loading={loadingSlots} date={date} slots={slots} booking={booking} onBook={book} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SlotsPanel({ loading, date, slots, booking, onBook }) {
  if (loading) return <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-blue-700" /></div>;
  if (!date) return <p className="text-sm text-slate-400">Scegli un giorno dal calendario per vedere gli orari disponibili.</p>;
  if (slots.length === 0) return <p className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">Nessuno slot disponibile in questa data.</p>;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="slots-grid">
      {slots.map((t) => (
        <Button key={t} variant="outline" disabled={booking} className="hover:border-blue-700 hover:bg-sky-50 hover:text-blue-700" onClick={() => onBook(t)} data-testid={`slot-${t}`}>
          {t}
        </Button>
      ))}
    </div>
  );
}

function ExchangeDialog({ info, onClose, onProposed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/isf/exchange/candidates/${info.medicoId}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [info.medicoId]);

  const candidates = data?.candidates || [];

  const propose = async (targetId) => {
    try {
      await api.post("/isf/exchange", { my_appointment_id: info.myApptId, target_appointment_id: targetId });
      toast.success("Proposta di scambio inviata");
      onProposed();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="exchange-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Proponi scambio · Dr. {info.medicoNome}</DialogTitle>
          <DialogDescription>Scegli l'appuntamento di un altro informatore con cui scambiare il tuo slot.</DialogDescription>
        </DialogHeader>
        {loading && <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-blue-700" /></div>}
        {!loading && !candidates.length && (
          <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nessun altro appuntamento disponibile per lo scambio presso questo medico.</p>
        )}
        {!loading && candidates.length > 0 && (
          <div className="space-y-2">
            {candidates.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`exchange-candidate-${c.id}`}>
                <div>
                  <p className="text-sm font-600 text-slate-900">{c.isf_nome}</p>
                  <p className="flex items-center gap-1 text-xs text-slate-500"><Building2 className="h-3 w-3" /> {c.isf_azienda}</p>
                  <p className="mt-0.5 text-xs capitalize text-blue-700">{fmtDate(c.date)} · {c.time}</p>
                </div>
                <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={() => propose(c.id)} data-testid={`propose-to-${c.id}`}>
                  <ArrowRightLeft className="mr-1 h-3.5 w-3.5" /> Proponi
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
